<script
  setup
  lang="ts"
  generic="
    TForm extends {
      setFieldValue: (...args: any[]) => void
      useStore: <TSelected>(
        selector: (state: { values: Record<string, unknown> }) => TSelected
      ) => Ref<TSelected>
    }
  "
>
  import { computed, ref, watch, type Ref } from 'vue'
  import {
    ActionCard,
    ActionCardLeadIcon,
    ActionCardTitle,
    Button,
    Popover,
    PopoverContent,
    PopoverTrigger,
    Toggle
  } from '@gat-ship/vue-components'
  import { useAccessRoles } from '../composables/useAccessRoles'
  import {
    IconChevronRight,
    IconEdit,
    IconEditOff,
    IconAccessRole
  } from '@gat-ship/icons'
  import type {
    FieldEditableOption,
    FieldVisibilityOption
  } from '@gat-ship/types'

  const props = defineProps<{
    form: TForm
  }>()

  const editablePopoverOpen = ref(false)
  const localEditableRoleIds = ref<string[]>([])
  const localEditableOption = ref<FieldEditableOption | undefined>(undefined)
  const step = ref(0)

  const dataSource = useAccessRoles()!

  const roleItems = computed(() =>
    dataSource.accessRoles.value.map((r) => ({ title: r.name, value: r.id }))
  )

  const visibilityOption = props.form.useStore(
    (s) => s.values.visibilityOption as FieldVisibilityOption | undefined
  )

  const visibleForRoleIds = props.form.useStore(
    (s) => s.values.visibleForRoleIds as string[] | undefined
  )

  const filteredRoleItems = computed(() => {
    if (visibilityOption.value !== 'visible-for-role') return roleItems.value
    const allowed = visibleForRoleIds.value ?? []
    return roleItems.value.filter((r) => allowed.includes(r.value))
  })

  const editableOption = props.form.useStore(
    (s) => s.values.editableOption as FieldEditableOption | undefined
  )

  const editableRoleIds = props.form.useStore(
    (s) => s.values.editableForRoleIds as string[] | undefined
  )

  watch(editablePopoverOpen, (open) => {
    if (open) {
      localEditableRoleIds.value = [...(editableRoleIds.value ?? [])]
      localEditableOption.value = editableOption.value ?? undefined
      step.value = 0
    }
  })

  function toggleEditableRole(roleId: string, pressed: boolean) {
    const current = localEditableRoleIds.value
    localEditableRoleIds.value = pressed
      ? [...new Set([...current, roleId])]
      : current.filter((id) => id !== roleId)
  }

  const editableRoleCount = computed(() => editableRoleIds.value?.length ?? 0)

  function handleSave() {
    props.form.setFieldValue('editableForRoleIds', localEditableRoleIds.value)
    props.form.setFieldValue('editableOption', localEditableOption.value)
    editablePopoverOpen.value = false
  }

  function handleEditableOptionChange(value: FieldEditableOption) {
    if (value === 'editable' || value === 'disabled') {
      localEditableOption.value = value
      localEditableRoleIds.value = []
      handleSave()
    } else if (value === 'editable-for-role') {
      localEditableOption.value = value
      step.value = 1
    }
  }

  const isSaveDisabled = computed(() => {
    if (localEditableOption.value === 'editable-for-role') {
      return localEditableRoleIds.value.length === 0
    }
    return localEditableOption.value === undefined
  })
</script>

<template>
  <Popover v-model:open="editablePopoverOpen" :modal="true">
    <PopoverTrigger as-child>
      <ActionCard>
        <div class="flex w-full flex-col gap-4">
          <div class="flex w-full items-center gap-2">
            <ActionCardLeadIcon
              :icon="editableOption === 'disabled' ? IconEditOff : IconEdit"
            />
            <div class="flex w-full items-center">
              <div>
                <ActionCardTitle> Editability </ActionCardTitle>
              </div>
              <div class="text-muted-foreground ml-auto text-sm font-normal">
                <span v-if="editableOption === 'editable'"> All Users </span>
                <span v-else-if="editableOption === 'editable-for-role'">
                  {{ editableRoleCount }} roles
                </span>
                <span v-else> Read-only </span>
              </div>
            </div>
          </div>
        </div>
      </ActionCard>
    </PopoverTrigger>
    <PopoverContent
      class="flex w-[var(--reka-popover-trigger-width)] flex-col gap-5"
    >
      <div class="flex flex-col gap-6">
        <div v-if="step === 0" class="flex flex-col gap-2">
          <p class="mb-2 text-sm font-normal">
            Who should be able to edit this field?
          </p>
          <div
            class="divide-border border-border flex flex-col divide-y border-t"
          >
            <button
              v-if="editableOption === 'editable-for-role'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleEditableOptionChange('editable-for-role')"
            >
              <IconAccessRole class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">Change role selection</div>
                <div class="text-muted-foreground text-xs font-normal">
                  Change the roles that can edit this field.
                </div>
              </div>
              <IconChevronRight class="text-muted-foreground ml-auto size-4" />
            </button>
            <button
              v-if="editableOption !== 'editable-for-role'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleEditableOptionChange('editable-for-role')"
            >
              <IconAccessRole class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">Users with specific roles</div>
                <div class="text-muted-foreground text-xs font-normal">
                  This field will be editable by users with the selected roles.
                </div>
              </div>
              <IconChevronRight class="text-muted-foreground ml-auto size-4" />
            </button>
            <button
              v-if="editableOption !== 'editable'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleEditableOptionChange('editable')"
            >
              <IconEdit class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">All users</div>
                <div class="text-muted-foreground text-xs font-normal">
                  This field will be editable by all users who can see this
                  field.
                </div>
              </div>
            </button>

            <button
              v-if="editableOption !== 'disabled'"
              class="hover:bg-secondary/80 flex w-full cursor-pointer items-center gap-3 px-3 py-3 text-base font-normal"
              @click="handleEditableOptionChange('disabled')"
            >
              <IconEditOff class="size-4" />
              <div class="flex flex-col items-start">
                <div class="text-sm">No users (read-only)</div>
                <div class="text-muted-foreground text-xs font-normal">
                  This field will not be editable by any users.
                </div>
              </div>
            </button>
          </div>
        </div>
        <template v-if="step === 1">
          <div class="flex flex-col gap-2">
            <span class="mb-2 text-sm font-normal"
              >Who should be able to edit this field?</span
            >

            <div class="flex flex-wrap gap-2">
              <Toggle
                v-for="role in filteredRoleItems"
                :key="role.value"
                variant="default"
                size="sm"
                :model-value="localEditableRoleIds.includes(role.value)"
                class="bg-secondary hover:bg-secondary/80 rounded-full pr-3 text-sm font-normal"
                @update:model-value="
                  (pressed: boolean) => toggleEditableRole(role.value, pressed)
                "
              >
                <IconAccessRole class="size-4" />
                {{ role.title }}
              </Toggle>
              <div
                v-if="filteredRoleItems.length === 0"
                class="text-muted-foreground text-xs italic"
              >
                No roles available.
              </div>
            </div>
          </div>
        </template>
        <div v-if="step === 1" class="flex items-center gap-2">
          <p class="text-muted-foreground text-xs font-normal">
            Select the roles that should be able to edit this field. You may
            select 1 or more roles.
          </p>
          <Button class="ml-auto" variant="ghost" size="sm" @click="step = 0">
            Back
          </Button>
          <Button
            variant="default"
            size="sm"
            :disabled="isSaveDisabled"
            @click="handleSave"
          >
            Confirm
          </Button>
        </div>
      </div>
    </PopoverContent>
  </Popover>
</template>
